import layouts from '@/features/catalogue/facilityShowcaseLayouts.json';
import type { CameraPose, FacilityInterior, FacilityRoom } from '../types';

const sourceUrl =
  'https://www.nesdis.noaa.gov/our-satellites/currently-flying/goes-east-west/geostationary-satellites-ground-systems';
type RoomDefinition = [string, string, FacilityRoom['kind'], string, [string, string][]];
const definitions: Record<string, RoomDefinition[]> = {
  'wallops-cdas': [
    [
      'receive',
      'RF reception',
      'racks',
      'Receive telemetry and instrument data; recover the digital stream.',
      [
        ['RF interface', 'Illustrative downconversion and receiver chain.'],
        ['Demodulation', 'Recover symbols and frames from the received signal.'],
        ['Data acquisition', 'Capture and stage the received data for onward transport.'],
        ['Timing reference', 'Distribute a common time and frequency reference.'],
      ],
    ],
    [
      'operations',
      'Station operations',
      'control',
      'Supervise the station, acquisition and command interfaces.',
      [
        ['Acquisition desk', 'Illustrative station monitoring console.'],
        ['Command desk', 'Illustrative command coordination console; no operational controls.'],
        ['Engineering desk', 'Illustrative engineering diagnostics console.'],
      ],
    ],
    [
      'transport',
      'Network & handoff',
      'racks',
      'Transfer received data and coordinate the ground system.',
      [
        ['Data buffer', 'Illustrative staging of acquired data.'],
        ['Network interface', 'Illustrative terrestrial data handoff to Suitland.'],
        ['Redundant interface', 'Representative alternate communications equipment.'],
        ['Timing & monitoring', 'Representative interface monitoring.'],
      ],
    ],
    [
      'services',
      'Power & cooling',
      'services',
      'Representative support equipment serving this illustrative room layout.',
      [
        ['Power distribution', 'Representative power distribution cabinet.'],
        ['UPS cabinet', 'Illustrative stored-energy power support; capacity unspecified.'],
        ['Cooling plant', 'Representative room cooling unit; capacity unspecified.'],
      ],
    ],
  ],
  suitland: [
    [
      'ingest',
      'Data ingest',
      'racks',
      'Receive ground-system data and stage it for processing.',
      [
        ['Ground network interface', 'Illustrative incoming interface from Wallops.'],
        ['Ingest services', 'Representative data ingestion and buffering.'],
        ['Quality screening', 'Illustrative input completeness and quality checks.'],
        ['Staging storage', 'Representative short-term processing storage.'],
      ],
    ],
    [
      'processing',
      'Product processing',
      'racks',
      'Illustrate processing from received observations to environmental products.',
      [
        ['Calibration services', 'Illustrative calibration and geolocation processing.'],
        ['Product generation', 'Illustrative higher-level environmental product generation.'],
        ['Processing storage', 'Representative working storage.'],
        [
          'Processing services',
          'Representative compute services; physical/cloud allocation is not asserted.',
        ],
        ['Service monitoring', 'Representative processing service monitoring.'],
        ['Power & cooling', 'Representative rack power and thermal support.'],
      ],
    ],
    [
      'operations',
      'Mission operations',
      'control',
      'Mission supervision and enterprise management are documented NSOF functions.',
      [
        ['Mission desk', 'Illustrative mission operations console.'],
        ['Product desk', 'Illustrative product operations console.'],
        ['Engineering desk', 'Illustrative ground-system engineering console.'],
      ],
    ],
    [
      'distribution',
      'Product distribution',
      'racks',
      'Distribute environmental products to downstream users.',
      [
        ['Product staging', 'Representative staging of completed products.'],
        ['Distribution services', 'Illustrative product delivery interfaces.'],
        ['User access', 'Representative downstream user access services.'],
        ['Service monitoring', 'Illustrative distribution monitoring.'],
      ],
    ],
  ],
};

export function facilityInterior(id: string): FacilityInterior | undefined {
  const layout = layouts[id as keyof typeof layouts];
  if (!layout) return;
  const width = layout.width / 2 - 0.9,
    depth = layout.depth / 2 - 0.9;
  return {
    ...layout,
    center: layout.center as [number, number],
    sourceUrl,
    sourceTitle: 'NOAA ground-system functions',
    rooms: definitions[id].map(([key, label, kind, purpose, equipment], i) => ({
      id: `room_${key}`,
      label,
      kind,
      purpose,
      width,
      depth,
      center: [(i % 2 ? 1 : -1) * (layout.width / 4), (i < 2 ? -1 : 1) * (layout.depth / 4)],
      equipment: equipment.map(([label, fn], n) => ({ id: `${key}_equipment_${n}`, label, function: fn })),
    })),
  };
}

/** Interior coordinates remain in the campus frame, allowing a continuous camera flight. */
export function interiorPoint(
  interior: FacilityInterior,
  x: number,
  y: number,
  z: number,
): [number, number, number] {
  const c = Math.cos(interior.rotation),
    s = Math.sin(interior.rotation);
  return [interior.center[0] + c * x + s * z, y, interior.center[1] - s * x + c * z];
}
export function roomPose(interior: FacilityInterior, room?: FacilityRoom): CameraPose {
  const [x, z] = room?.center ?? [0, 0];
  const distance = room
    ? Math.max(room.width, room.depth) * 0.9
    : Math.max(interior.width, interior.depth) * 0.85;
  return {
    target: interiorPoint(interior, x, 0.8, z),
    position: interiorPoint(interior, x + distance * 0.4, distance * 0.95, z + distance * 0.85),
    fov: 45,
  };
}
